/**
 * universalError (PHASE 7)
 * ========================
 * Frontend-normalized representation of the backend universal error schema:
 *   { error: true, code, message, details }
 *
 * Goals:
 * - Work for HTTP (axios/fetch) and SSE frames
 * - Never throw during parsing
 * - Preserve structured details for "See details" drawers
 */

export type UniversalErrorSchema = {
  error: true;
  code?: string;
  message?: string;
  details?: any;
  // allow extra fields from SSE frames
  [k: string]: any;
};

export type UniversalErrorSource = 'http' | 'sse' | 'polling' | 'ui';

export type UIUniversalError = {
  id: string;
  source: UniversalErrorSource;
  code: string;
  message: string;
  details?: any;
  httpStatus?: number;
  timestamp: string;
  stack?: string;
  raw?: any;
  // "severity" is not enforced by backend, but helps UI decide banners vs inline.
  severity?: 'info' | 'warning' | 'error' | 'critical';
};

export function isUniversalErrorSchema(v: any): v is UniversalErrorSchema {
  return v && typeof v === 'object' && v.error === true;
}

export function isUIUniversalError(v: any): v is UIUniversalError {
  return (
    v &&
    typeof v === 'object' &&
    typeof v.id === 'string' &&
    typeof v.code === 'string' &&
    typeof v.message === 'string' &&
    typeof v.timestamp === 'string'
  );
}

function safeString(v: any): string {
  try {
    if (typeof v === 'string') return v;
    if (v == null) return '';
    return String(v);
  } catch {
    return '';
  }
}

function genId(prefix = 'err'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function defaultSeverity(code: string, httpStatus?: number): UIUniversalError['severity'] {
  const c = String(code || '').toUpperCase();
  if (c.includes('POLICY') || c.includes('FORBIDDEN')) return 'warning';
  if (c.includes('TIMEOUT') || c.includes('UNAVAILABLE') || c.includes('NETWORK')) return 'error';
  if (httpStatus && httpStatus >= 500) return 'error';
  if (httpStatus && httpStatus === 401) return 'warning';
  if (httpStatus && httpStatus === 403) return 'warning';
  return 'error';
}

/**
 * Normalize any error-like input into UIUniversalError.
 *
 * Accepts:
 * - Universal schema objects: {error:true, code, message, details}
 * - SSE error frames: {type:"error", error:true, ...}
 * - axios errors (error.response.data may contain universal schema)
 * - fetch errors / Error instances
 * - strings
 */
export function normalizeUniversalError(
  input: any,
  opts: {
    source?: UniversalErrorSource;
    httpStatus?: number;
    fallbackCode?: string;
    fallbackMessage?: string;
    raw?: any;
  } = {}
): UIUniversalError {
  try {
    if (isUIUniversalError(input)) return input;

    const source: UniversalErrorSource = opts.source || 'ui';
    const timestamp = new Date().toISOString();

    // Axios-like error (best effort without importing axios types)
    const axiosStatus = input?.response?.status;
    const axiosData = input?.response?.data;
    if (isUniversalErrorSchema(axiosData)) {
      const code = safeString(axiosData.code || opts.fallbackCode || 'UNKNOWN_ERROR') || 'UNKNOWN_ERROR';
      const httpStatus = Number.isFinite(axiosStatus) ? Number(axiosStatus) : opts.httpStatus;
      const message =
        safeString(axiosData.message) ||
        safeString(opts.fallbackMessage) ||
        'An unexpected error occurred';
      return {
        id: genId(source),
        source,
        code,
        message,
        details: axiosData.details ?? axiosData,
        httpStatus,
        timestamp,
        stack: safeString(input?.stack),
        raw: opts.raw ?? input,
        severity: defaultSeverity(code, httpStatus),
      };
    }

    // Universal schema / SSE error frame payload
    if (isUniversalErrorSchema(input)) {
      const code = safeString(input.code || opts.fallbackCode || 'UNKNOWN_ERROR') || 'UNKNOWN_ERROR';
      const message =
        safeString(input.message) ||
        safeString(opts.fallbackMessage) ||
        'An unexpected error occurred';
      const httpStatus = opts.httpStatus;
      return {
        id: genId(source),
        source,
        code,
        message,
        details: input.details ?? input,
        httpStatus,
        timestamp,
        stack: safeString(input?.stack),
        raw: opts.raw ?? input,
        severity: defaultSeverity(code, httpStatus),
      };
    }

    // Error instance
    if (input instanceof Error) {
      const code = safeString((input as any).code || opts.fallbackCode || 'UNKNOWN_ERROR') || 'UNKNOWN_ERROR';
      const message =
        safeString(input.message) ||
        safeString(opts.fallbackMessage) ||
        'An unexpected error occurred';
      const httpStatus = opts.httpStatus;
      return {
        id: genId(source),
        source,
        code,
        message,
        details: (input as any).details,
        httpStatus,
        timestamp,
        stack: safeString(input.stack),
        raw: opts.raw ?? input,
        severity: defaultSeverity(code, httpStatus),
      };
    }

    // String / unknown
    const message =
      safeString(input) ||
      safeString(opts.fallbackMessage) ||
      'An unexpected error occurred';
    const code = safeString(opts.fallbackCode || 'UNKNOWN_ERROR') || 'UNKNOWN_ERROR';
    return {
      id: genId(source),
      source,
      code,
      message,
      details: undefined,
      httpStatus: opts.httpStatus,
      timestamp,
      stack: undefined,
      raw: opts.raw ?? input,
      severity: defaultSeverity(code, opts.httpStatus),
    };
  } catch (e) {
    return {
      id: genId('ui'),
      source: opts.source || 'ui',
      code: 'ERROR_NORMALIZATION_FAILED',
      message: 'Failed to parse error payload',
      details: { cause: safeString((e as any)?.message) },
      timestamp: new Date().toISOString(),
      raw: { input, opts },
      severity: 'error',
    };
  }
}

export function summarizeUniversalError(e: UIUniversalError): { title: string; summary: string } {
  const code = safeString(e?.code || 'UNKNOWN_ERROR');
  const message = safeString(e?.message || 'An unexpected error occurred');

  // Title should be short + stable; summary can be user-facing.
  const title = code.replace(/_/g, ' ');
  const summary = message;
  return { title, summary };
}

